import {
  Breadcrumb,
  Button,
  Col,
  Drawer,
  Empty,
  Flex,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Table,
  Tag,
  Tooltip,
  Typography,
  notification,
  theme,
} from "antd";
import {
  HomeOutlined,
  DeleteOutlined,
  EyeOutlined,
  EditOutlined,
  LockOutlined,
  UnlockOutlined,
  FilterOutlined,
  WarningOutlined,
  CaretRightOutlined,
  EnterOutlined,
} from "@ant-design/icons";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import MasterDataAPI from "../../restAPI/MasterDataAPI";
import FilterComponent from "../../utils/FilterComponent";
import CommonSkeletonLoader from "../../helperComponents/CommonSkeleton";

const { Search } = Input;
const { Text, Link } = Typography;

export default function CommitteeConfig() {
  const { companyId, token, role } = useSelector((state) => state.auth);
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);
  const [btnPermission, setBtnPermission] = useState({
    "add": false,
    "edit": false,
    "delete": false,
    "view": false,
    "activeInactive": false
  });
  const [committeeId, setCommitteeId] = useState("");
  const [initialCommitteeData, setInitialCommitteeData] = useState({ name: "", description: "" });
  const [actionState, setActionState] = useState("add");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [payload, setPayload] = useState({
    code: "",
    name: "",
    description: "",
  });
  const [filterValues, setFilterValues] = useState([]);
  const [committeeData, setCommitteeData] = useState([]);
  const [tempCommitteeData, setTempCommitteeData] = useState([]);
  const [open, setOpen] = useState(false);
  const [open2, setOpen2] = useState(false);
  const [openFIlter, setOpenFilter] = useState(false);
  const [popConfirmOpen, setPopConfirmOpen] = useState(false);
  const [cancelButtonDisabled, setCancelButtonDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDataLoading, setDataLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [api, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = (type, message, description, placement) => {
    api[type]({
      message: message,
      description: description,
      placement,
    });
  };

  useEffect(() => {
    setDataLoading(true);
    getCommitteeComfigList();
  }, [companyId]);

  const applySearchAndFilters = (data, searchText, filters) => {
    return data.filter((committeeConfig) => {
      const matchesSearch = searchText ? 
        (committeeConfig.code || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (committeeConfig.name || '').toLowerCase().includes(searchText.toLowerCase()) 
        : true

      const matchesFilters = filters.length > 0 ?
        filters.every(({ key, value }) => {
          if (!value) return false;
          const itemValue = (committeeConfig[key]?.toString() || '').toLowerCase();
          return itemValue.startsWith(value.toLowerCase());
        })
        : true

      return matchesSearch && matchesFilters
    })
  }

  const getCommitteeComfigList = async (isFirstTime = true) => {
    try {
      const response = await MasterDataAPI.getCommitteeTypeDataByCompanyId(
        companyId,
        token
      );
      if (response.data.data) {
        if (!isFirstTime) {
          const updatedCommitteeList = response.data.data
          setCommitteeData(updatedCommitteeList)
          const updatedTempList = applySearchAndFilters(updatedCommitteeList, searchInputValue, filterValues)
          setTempCommitteeData(updatedTempList);
        }

        if (isFirstTime) {
          setCommitteeData(response?.data?.data || []);
          setTempCommitteeData(response?.data?.data || []);
          setDataLoading(false);
        }
      } else {
        setCommitteeData([]);
        setTempCommitteeData([]);
        setDataLoading(false);
      }
    } catch (error) {
      setCommitteeData([]);
      setTempCommitteeData([]);
      setDataLoading(false);
      console.log("Error in getCommittee config ", error);
      openNotificationWithIcon("error", "Error", `Get committee type data error`, "bottomLeft");
    }
  };

  const showDrawer = () => {
    setActionState('add');
    setInitialCommitteeData({ name: "", description: "" });
    setPayload({
      code: "",
      name: "",
      description: "",
    });
    form.setFieldsValue({
      name: "",
      description: "",
    });
    setOpen(true);
  };
  const onClose = () => {
    form.resetFields();
    setInitialCommitteeData({ name: "", description: "" });
    setPayload({
      code: "",
      name: "",
      description: "",
    });
    getCommitteeComfigList(false);
    setOpen(false);
    setPopConfirmOpen(false);
    setCommitteeId("");
    setTimeout(() => {
      setActionState('add')
    }, 200)
  };

  const showFilterDrawer = () => {
    setOpenFilter(true);
  };
  const onCloseFilter = () => {
    setOpenFilter(false);
  };

  const getCommitteeTypeRules = useCallback(() => [
    {
      required: true,
      message: 'Committee type is required',
    },
    {
      max: 50,
      message: 'Committee type must be less than 50 characters',
    },
    {
      validator: (_, value) => {
        if (value && value.trim().length === 0) {
          return Promise.reject(new Error('Committee Type is not valid.'));
        }
        return Promise.resolve();
      },
    },
  ], []);

  const getCommitteeDescriptionRules = useCallback(() => [
    {
      max: 250,
      message: 'Committee description must be less than 250 characters',
    },
  ], []);

  const isFormModified = useCallback(() => {
    if (actionState !== 'edit') return true;
    
    if (!formValues) return false;
    
    const currentValues = form.getFieldsValue();
    const nameChanged = (currentValues.name || '').trim() !== (initialCommitteeData.name || '').trim();
    const descriptionChanged = (currentValues.description || '').trim() !== (initialCommitteeData.description || '').trim();
    
    return nameChanged || descriptionChanged;
  }, [actionState, formValues, initialCommitteeData, form]);

  const showDrawer2 = () => {
    setOpen2(true);
  };
  const onClose2 = () => {
    setOpen2(false);
    setPayload({
      code: "",
      name: "",
      description: "",
    });
  };

  const {
    token: { colorBgContainer, borderRadiusLG, colorPrimary, colorBorder },
  } = theme.useToken();

  const columns = [
    {
      title: "Committee Type Code",
      dataIndex: "code",
      key: "code",
    },
    {
      title: "Committee Type Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      fixed: "right",
      render: (_, record) =>
        record.status ? (
          <Tag color="purple" style={{ width: 90, textAlign: "center" }}>
            Active
          </Tag>
        ) : (
          <Tag color="warning" style={{ width: 90, textAlign: "center" }}>
            Inactive
          </Tag>
        ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <Flex gap="small" justify="center" align="center" vertical>
          <Flex gap="middle" wrap="wrap">
            {/* edit icon */}
            {btnPermission.edit && (
              <Tooltip title="Edit Committee Type" placement="top">
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => editHandler(record)}
                  disabled={!record.status}
                />
              </Tooltip>
            )}

            {/* View icon */}
            {btnPermission.view && (
              <Tooltip title="View Committee Type" placement="top">
                <Button
                  icon={<EyeOutlined />}
                  size="small"
                  onClick={() => viewHandler(record)}
                />
              </Tooltip>
            )}

            {/* lock icon */}
            {
              btnPermission.activeInactive && (
                <Tooltip title="Active/Inactive Committee Type" placement="top">
                  <Popconfirm
                    title={
                      record.status
                        ? "Do you want to inactivate the committee type?"
                        : "Do you want to reactivate the committee type?"
                    }
                    onConfirm={() => editStatusHandler(record)}
                    onCancel={() =>
                      openNotificationWithIcon("error", "Abort", `Status update cancelled`, "bottomLeft")
                    }
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button size="small" icon={record.status ? <LockOutlined /> : <UnlockOutlined />} />
                  </Popconfirm>
                </Tooltip>
              )
            }
            {/* delete icon */}
            {btnPermission.delete && (
              <Tooltip title="Delete Committee Type" placement="top">
                <Popconfirm
                  title="Do you want to delete this committee Type?"
                  onConfirm={() => deleteHandler(record)}
                  onCancel={() =>
                    openNotificationWithIcon("error", "Abort", `Delete cancelled`, "bottomLeft")
                  }
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    danger
                    type="primary"
                    icon={<DeleteOutlined />}
                    size="small"
                    disabled={record.status}
                  />
                </Popconfirm>
              </Tooltip>
            )}
          </Flex>
        </Flex>
      ),
    },
  ];

  const editHandler = (record) => {
    setCommitteeId(record.id);
    const initialValues = {
      name: record.name,
      description: record.description || "",
    };
    setInitialCommitteeData(initialValues);
    setPayload({
      code: record.code,
      name: record.name,
      description: record.description,
    });
    form.setFieldsValue(initialValues);
    setActionState("edit");
    setOpen(true);
  };

  const viewHandler = (record) => {
    setCommitteeId(record.id);
    setPayload({
      code: record.code,
      name: record.name,
      description: record.description,
      status: record.status,
    });
    setActionState("view");
    showDrawer2();
  };

  const editStatusHandler = async (records) => {
    const formData = {
      status: !records.status,
    };

    await MasterDataAPI.updateStatusCommitteeConfig(
      records.id,
      formData,
      companyId,
      token
    )
      .then((res) => {
        openNotificationWithIcon("success", "Successful", `${res.message}`, "bottomLeft");
        // setSearchInputValue('');
        // setFilterValues([]);
        getCommitteeComfigList(false);
      })
      .catch((err) => {
        if (err.response) {
          openNotificationWithIcon("error", "Committee type status change failed", `${err.response.data.message}`, "bottomLeft");
        } else {
          openNotificationWithIcon("error", "Committee type status change failed", `${err.message}`, "bottomLeft");
        }
      });
  };

  const deleteHandler = async (records) => {
    await MasterDataAPI.deleteCommitteeConfig(records.id, companyId, token)
      .then((res) => {
        openNotificationWithIcon("success", "Successful", `${res.message}`, "bottomLeft");
        getCommitteeComfigList(false);
      })
      .catch((err) => {
        if (err.response) {
          openNotificationWithIcon("error", "Committee type deletion failed", `${err.response.data.message}`, "bottomLeft");
        } else {
          openNotificationWithIcon("error", "Committee type deletion failed", `${err.message}`, "bottomLeft");
        }
      });
  };


  const handleSubmit = async () => {
    const validation = await validateBeforeSubmit();
    if (!validation) {
      return;
    }

    setLoading(true);
    const formValues = form.getFieldsValue();
    const trimmedPayload = {
      name: (formValues.name || "").trim(),
      description: (formValues.description || "").trim(),
    };

    if (actionState === "add") {
      try {
        const { message } = await MasterDataAPI.createCommitteeConf(trimmedPayload, companyId, token);
        openNotificationWithIcon("success", "Successful", `${message}`, "bottomLeft");
        onClose();
      } catch (error) {
        if (error.response) {
          openNotificationWithIcon("error", "Committee type creation failed", `${error.response.data.message}`, "bottomLeft");
        } else {
          openNotificationWithIcon("error", "Committee type creation failed", `${error.message}`, "bottomLeft");
        }
      } finally {
        setLoading(false);
      }
    }

    if (actionState === "edit") {
      const editPayload = {
        ...trimmedPayload,
        code: payload.code, // Keep the code from payload
      };
      try {
        const { message } = await MasterDataAPI.updateCommitteeConfig(
          committeeId,
          editPayload,
          companyId,
          token
        );
        openNotificationWithIcon("success", "Successful", `${message}`, "bottomLeft");
        onClose();
      } catch (error) {
        if (error.response) {
          openNotificationWithIcon("error", "Committee type update failed", `${error.response.data.message}`, "bottomLeft");
        } else {
          openNotificationWithIcon("error", "Committee type update failed", `${error.message}`, "bottomLeft");
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const validateBeforeSubmit = async () => {
    try {
      await form.validateFields();
      return true;
    } catch (errorInfo) {
      return false;
    }
  };

  const handleCreateUpdateClick = async (e) => {
    e.preventDefault();
    let validation = await validateBeforeSubmit();

    if (actionState === "add" && validation) {
      handleSubmit();
    } else {
      if (validation) {
        setPopConfirmOpen(true);
      } else {
        setPopConfirmOpen(false);
      }
    }
  };

  const handleCancel = () => {
    setCancelButtonDisabled(true);
    openNotificationWithIcon(
      "error",
      "Abort",
      `${actionState === "add" ? "Create" : "Update"} cancelled`,
      "bottomLeft"
    );
    setTimeout(() => {
      setPopConfirmOpen(false);
      setCancelButtonDisabled(false);
    }, 300);
  };

  const handleSearchFunction = (value) => {
    setSearchInputValue(value);

    if (value) {
      if (filterValues.length > 0) {
        const filterData = tempCommitteeData.filter(
          (item) =>
            (item.name || '').toLowerCase().includes(value.toLowerCase()) ||
            (item.code || '').toLowerCase().includes(value.toLowerCase()) ||
            (item.description || '').toLowerCase().includes(value.toLowerCase())
        );
        setTempCommitteeData(filterData);
      } else {
        const filteredData = committeeData.filter(
          (item) =>
            (item.name || '').toLowerCase().includes(value.toLowerCase()) ||
            (item.code || '').toLowerCase().includes(value.toLowerCase())
        );
        setTempCommitteeData(filteredData);
      }
    } else {
      if (filterValues.length > 0) {
        const filterData = filterDataByCriteria(committeeData, filterValues);
        setTempCommitteeData(filterData);
      } else {
        setTempCommitteeData(committeeData);
      }
    }
  };

  const filterDataByCriteria = (data, criteria) => {
    return data.filter((item) => {
      return criteria.every(({ key, value }) => {
        if (!item[key] || !value) return false;

        const itemValue = (item[key]?.toString() || '').toLowerCase();
        const lowerCasePrefix = (value || '').toLowerCase();
        return itemValue.includes(lowerCasePrefix);
      });
    });
  };

  const handleFilterSearchFunction = (value) => {
    if (value) {
      if (searchInputValue.length > 0) {
        const filterData = committeeData.filter(
          (item) =>
            (item.name || '').toLowerCase().includes(searchInputValue.toLowerCase()) ||
            (item.code || '').toLowerCase().includes(searchInputValue.toLowerCase()) ||
            (item.description || '')
              .toLowerCase()
              .includes(searchInputValue.toLowerCase())
        );
        const filterData2 = filterDataByCriteria(filterData, value);
        setTempCommitteeData(filterData2);
      } else {
        const filterData = filterDataByCriteria(committeeData, value);
        setTempCommitteeData(filterData);
      }
    } else {
      if (searchInputValue?.length > 0) {
        const filteredData = committeeData.filter(
          (item) =>
            (item.name || '').toLowerCase().includes(searchInputValue.toLowerCase()) ||
            (item.code || '').toLowerCase().includes(searchInputValue.toLowerCase()) ||
            (item.description || '')
              .toLowerCase()
              .includes(searchInputValue.toLowerCase())
        );
        setTempCommitteeData(filteredData);
      } else {
        setTempCommitteeData(committeeData);
      }
    }
  };

  const handleFilterDetailsChange = (value) => {
    setFilterValues(value);
  };
  const handleRemoveTag = (key) => {
    setFilterValues((prev) => prev.filter((item) => item.key !== key));
  };

  useEffect(() => {
    setBtnPermission({
      "add": false,
      "edit": false,
      "delete": false,
      "view": false,
      "activeInactive": false
    });

    if (role.length > 0) {
      role.forEach((value) => {
        if (value.restricted) {
          if (value.roleName === "Company Admin") {
            setBtnPermission({
              "add": true,
              "edit": true,
              "delete": true,
              "view": true,
              "activeInactive": true
            });
          } else if (value.roleName === "Admin") {
            setBtnPermission({
              "add": true,
              "edit": true,
              "delete": true,
              "view": true,
              "activeInactive": true
            })
          }
        } else if (value?.permissions) {
          const permissionData = value.permissions.find(
            (data) => data.permissionName === "Committee Type"
          );

          if (permissionData) {
            permissionData.permissionLevel.map((value) => {
              switch (value) {
                case "create":
                  setBtnPermission((prevState) => ({
                    ...prevState,
                    add: true,
                  }));
                  break;
                case "view":
                  setBtnPermission((prevState) => ({
                    ...prevState,
                    view: true,
                  }));
                  break;
                case "edit":
                  setBtnPermission((prevState) => ({
                    ...prevState,
                    edit: true,
                  }));
                  break;
                case "delete":
                  setBtnPermission((prevState) => ({
                    ...prevState,
                    delete: true,
                  }));
                  break;
                case "Active / Inactive":
                  setBtnPermission(prevState => ({
                    ...prevState,
                    activeInactive: true
                  }));
                  break;
              }
            });
          } else {
            setBtnPermission({
              "add": false,
              "edit": false,
              "delete": false,
              "view": false,
              "activeInactive": false
            });
          }
        }
      });
    }
  }, []);

  useEffect(() => {
    handleFilterSearchFunction(filterValues);
    setCurrentPage(1); // Reset to page 1 when filters change
  }, [filterValues]);

  useEffect(() => {
    handleSearchFunction(searchInputValue);
    setCurrentPage(1); // Reset to page 1 when search changes
  }, [searchInputValue]);

  const filterTags = filterValues.filter(filterObject => filterObject.value.trim() !== "");

  return (
    <>
      <Flex
        gap={20}
        justify="space-between"
        align="center"
        style={{
          margin: "16px",
          padding: "16px 24px",
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Breadcrumb
          items={[
            {
              title: <HomeOutlined />,
            },
            {
              title: "Master Data / Committee Type",
            },
          ]}
        />
        <Flex justify="center" align="center" gap={12}>
          <Flex justify="center" align="center" gap={12}>
            {btnPermission.view && (
              <>
                <Button
                  onClick={showFilterDrawer}
                  icon={
                    <FilterOutlined
                      style={{ paddingLeft: 8, paddingRight: 8 }}
                    />
                  }
                />
                <Search
                  placeholder="Search"
                  value={searchInputValue}
                  onChange={(e) => setSearchInputValue(e.target.value)}
                />
              </>
            )}
          </Flex>
          {btnPermission.add && (
            <Button type="primary" onClick={showDrawer}>
              Add New
            </Button>
          )}
        </Flex>
      </Flex>
      <div style={{ margin: "16px" }}>
        {filterTags.length !== 0 && (
          <Flex align="center" gap={12}>
            {filterTags.map((item) => (
              <Tag
                bordered={false}
                closable
                color="success"
                key={item?.key}
                onClose={() => handleRemoveTag(item?.key)}
              >
                {item?.value}
              </Tag>
            ))}
          </Flex>
        )}
      </div>
      <div
        style={{
          margin: "16px",
          padding: 24,
          minHeight: "78svh",
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        {isDataLoading ? (
          <>
            <CommonSkeletonLoader />
          </>
        ) : (
          <>
            <Table
              columns={columns}
              dataSource={tempCommitteeData}
              scroll={{ x: "max-content" }}
              pagination={{
                current: currentPage,
                pageSize,
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                },
                onShowSizeChange: (current, size) => {
                  setCurrentPage(1);
                  setPageSize(size);
                },
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                selectProps: {
                  showSearch: false,
                  inputReadOnly: true,
                  onInputKeyDown: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  },
                  onKeyDown: (e) => {
                    // Allow only arrow keys, Enter, Escape, Tab
                    if (!['ArrowUp', 'ArrowDown', 'Enter', 'Escape', 'Tab'].includes(e.key)) {
                      e.preventDefault();
                    }
                  }
                }
              }}
            />
          </>
        )}
        {/* Drawer for Add/Edit */}
        <Drawer
          title={
            actionState === "add"
              ? "Add Committee Type"
              : "Update Committee Type"
          }
          onClose={onClose}
          open={open}
          footer={[
            <Flex justify="flex-end" align="center" gap="large">
              <Button onClick={onClose}>Cancel</Button>
              {actionState === "add" ? (
                <Button type="primary" onClick={handleSubmit} loading={loading}>
                  Create
                </Button>
              ) : (
                <Popconfirm
                  title="Do you want to update the Committee Type?"
                  open={popConfirmOpen}
                  onConfirm={() => handleSubmit()}
                  onCancel={handleCancel}
                  okText="Yes"
                  cancelText="No"
                  cancelButtonProps={{ disabled: cancelButtonDisabled }}
                >
                  <Button 
                    type="primary" 
                    onClick={handleCreateUpdateClick}
                    disabled={!isFormModified()}
                  >
                    Update
                  </Button>
                </Popconfirm>
              )}
            </Flex>,
          ]}
        >
          <Form 
            form={form}
            layout="vertical"
            initialValues={{
              name: "",
              description: "",
            }}
            onValuesChange={(changedValues, allValues) => {
              setPayload(prev => ({
                ...prev,
                name: allValues.name || "",
                description: allValues.description || "",
              }));
            }}
          >
            {actionState === "edit" && (
              <Form.Item label="Code">
                <Input
                  placeholder="Committee Code"
                  autoComplete="new-code"
                  disabled
                  value={payload["code"]}
                />
              </Form.Item>
            )}
            <Form.Item
              label="Committee Type"
              name="name"
              rules={getCommitteeTypeRules()}
            >
              <Input
                placeholder="Committee Type"
                autoComplete="new-name"
              />
            </Form.Item>
            <Form.Item
              label="Committee Description"
              name="description"
              rules={getCommitteeDescriptionRules()}
            >
              <Input.TextArea
                placeholder="Committee Description"
                autoComplete="new-description"
              />
            </Form.Item>
          </Form>
        </Drawer>

        {/* Drawer for View */}
        <Drawer
          title="View Committee Type"
          onClose={onClose2}
          open={open2}
        // footer={[
        //     <Flex justify='flex-end' align='center'>
        //         <Button onClick={onClose2}>
        //             Close
        //         </Button>
        //     </Flex>
        // ]}
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Text type="secondary"> Code </Text>
              <div>
                <Text strong> {payload["code"]}</Text>
              </div>
            </Col>
            <Col span={12}>
              <Text type="secondary">Status</Text>
              <div style={{ marginTop: 8 }}>
                {payload["status"] ? (
                  <Tag
                    color="purple"
                    style={{ width: 90, textAlign: "center" }}
                  >
                    Active
                  </Tag>
                ) : (
                  <Tag
                    color="warning"
                    style={{ width: 90, textAlign: "center" }}
                  >
                    Inactive
                  </Tag>
                )}
              </div>
            </Col>
            <Col span={24}>
              <Text type="secondary">Name</Text>
              <div>
                <Text strong>{payload["name"]}</Text>
              </div>
            </Col>
            <Col span={24}>
              <Text type="secondary">Description</Text>
              <div>
                <Text strong>{(payload["description"] || payload["description"].length > 0) ? payload["description"] : "N/A"}</Text>
              </div>
            </Col>
          </Row>
        </Drawer>

        <FilterComponent
          openDrawer={openFIlter}
          closeDrawer={onCloseFilter}
          data={tempCommitteeData}
          handleFilterDetailsChange={handleFilterDetailsChange}
          filterValues={filterValues}
          screenName="masterData_committee"
          resetFilterValues={() => setFilterValues([])}
        />
        {contextHolder}
      </div>
    </>
  );
}
